import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Percent, Calculator, CalendarClock, AlertTriangle } from "lucide-react";
import { GSTRateSheet } from "../components/tax/GSTRateSheet";
import { IncomeTaxCalculator } from "../components/tax/IncomeTaxCalculator";
import { AdvanceTaxCalculator } from "../components/tax/AdvanceTaxCalculator";
import WhatsAppButton from "../components/WhatsAppButton";

const tools = [
  { id: "gst", title: "GST Rate Sheet", desc: "Latest Indian GST slabs — 0%, 5%, 18% & 40%", icon: Percent },
  { id: "income-tax", title: "Income Tax Calculator", desc: "FY 2025-26 (AY 2026-27) — New vs Old regime", icon: Calculator },
  { id: "advance-tax", title: "Advance Tax Calculator", desc: "Installment schedule & due dates", icon: CalendarClock },
];

export default function TaxTools() {
  const [activeTool, setActiveTool] = useState("gst");

  return (
    <div className="min-h-screen bg-brand-white">
      <header className="fixed top-0 w-full bg-white border-b border-brand-border shadow-sm z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="taxtools-logo-link">
            <img src="/logo.png" alt="Medacc Consultancy" className="h-16 w-auto" data-testid="taxtools-header-logo" />
            <span className="hidden sm:inline text-xl font-bold text-brand-navy">Medacc Consultancy</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium" data-testid="taxtools-home-link">Home</Link>
            <a href="/#services" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium">Services</a>
            <a href="/#contact" className="text-brand-navy/80 hover:text-brand-orange transition-colors font-medium">Contact</a>
          </nav>
          <Link to="/" className="lg:hidden">
            <Button variant="ghost" size="sm" data-testid="taxtools-mobile-back" className="text-brand-navy">
              <ArrowLeft className="h-4 w-4 mr-1" /> Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="pt-28 pb-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-brand-navy mb-2">Tax Tools</h1>
            <p className="text-brand-muted">Free calculators & reference sheets, updated for the latest Indian tax rules</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {tools.map((t) => {
              const Icon = t.icon;
              const active = activeTool === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`tool-tab-${t.id}`}
                  onClick={() => setActiveTool(t.id)}
                  className={`text-left rounded-xl border-2 p-5 transition-all ${
                    active
                      ? "border-brand-orange bg-brand-orange/10 shadow-md"
                      : "border-brand-border bg-white hover:border-brand-orange/60 hover:shadow-sm"
                  }`}
                >
                  <Icon className={`h-7 w-7 mb-3 ${active ? "text-brand-orange" : "text-brand-muted"}`} />
                  <h3 className="font-semibold mb-1 text-brand-navy">{t.title}</h3>
                  <p className="text-xs text-brand-muted">{t.desc}</p>
                </button>
              );
            })}
          </div>

          <div className="mb-8">
            {activeTool === "gst" && <GSTRateSheet />}
            {activeTool === "income-tax" && <IncomeTaxCalculator />}
            {activeTool === "advance-tax" && <AdvanceTaxCalculator />}
          </div>

          <div className="bg-brand-grey border border-brand-border rounded-lg p-4 flex gap-3 items-start" data-testid="tax-tools-disclaimer">
            <AlertTriangle className="h-5 w-5 text-brand-muted mt-0.5 shrink-0" />
            <p className="text-sm text-brand-muted">
              <strong className="text-brand-navy">Disclaimer:</strong> For reference only, consult a tax professional for accurate filing. Rates and calculations are based on publicly available information and may not cover every scenario.
            </p>
          </div>

          <div className="mt-8 bg-brand-navy rounded-xl p-6 sm:p-8 text-center text-white">
            <h3 className="text-xl font-bold mb-2">Need help with your taxes?</h3>
            <p className="text-white/70 mb-4 text-sm">Our experts handle GST filing, income tax returns, and tax planning for you.</p>
            <Link to="/#contact">
              <Button data-testid="taxtools-contact-cta" className="bg-brand-orange text-white hover:bg-brand-orange-dark">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <WhatsAppButton />
    </div>
  );
}
